<template>
  <v-container class="lighten-5 d-flex flex-column flex-wrap align-content-start kds-screen ma-0 py-1"
  :class="dark_mode ? 'dark-mode' : 'blue-grey'">
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
      ></v-progress-circular>
    </v-overlay>
    <v-row style=" flex: 0; margin: 0 10px 0 0;">
          <KDSNavBar ref="navbar" />
      </v-row>
      <ItemSummaryBar v-if="settings.show_the_side_bar_priority_items" :orders="visible_orders" :priority_items="settings.selected_priority_items"/>
      <KDSOrder
      v-for="(order_id, i) in displayed_order_ids"
      :order_id="order_id"
      :key="order_id"
      :ref="'order_' + order_id"
      :selected="selected_order_id === order_id"
      :index="i + 1"
      :page_offset="page_offset"
      :style="{ zoom: settings.ui_scale || 1 }"
    />
    <SettingsDialog style="position: absolute;"/>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import KDSOrder from '../components/KDSOrder.vue'
import KDSNavBar from '../components/KDSNavBar.vue'
import SettingsDialog from '../components/SettingsDialog.vue'
import ItemSummaryBar from '../components/ItemSummaryBar.vue';
import { setup_api } from '../api/common'

export default {
  name: 'KDS',
  components: {
    KDSOrder,
    KDSNavBar,
    SettingsDialog,
    ItemSummaryBar
  },
  data: () => ({
    error: null,
    audioUnlocked: false,
    selected_order_id: null,
    notify_sound: null,
    hidden_timeout_id: null,
    completion_sync_interval_id: null,
    // Bump-bar pagination: tracks which block of 8 orders is currently active.
    // Only ever mutated by scrollPage(); never written to Vuex state.
    page_offset: 0,
    // 10-second inactivity timer — auto-resets to page 0 when user stops interacting.
    scroll_reset_timeout_id: null,
    // Web Serial
    serialPort: null,
    serialReader: null,
    serialReading: false,
  }),

  computed: {
    ...mapState(['orders', 'loading', 'settings', 'show_hidden', 'bumpbar_map', 'settings_dialog']),
    dark_mode: {
      get() {
        return this.settings?.dark_mode ?? false
      },
    },
    displayed_order_ids() {
      const hidden_state = this.show_hidden
      return Object.keys(this.orders).filter(oid => this.orders[oid].hidden == hidden_state)
    },
    visible_orders_count() {
      return Object.keys(this.orders).filter(oid => !this.orders[oid].hidden).length
    },
    visible_orders() {
      return Object.values(this.orders).filter(order => !order.hidden)
    },
    hidden_orders() {
      return Object.values(this.orders).filter(order => order.hidden)
    },
  },

  created() {
    // Load the sound once when the component is created
    // The 'require' happens immediately, and the Audio object is built.
    this.notify_sound = new Audio(require('@/assets/ding_dong.wav'));
    
    // Explicitly tell browser to preload the file data
    this.notify_sound.preload = 'auto'; 
  },

  watch: {
    show_hidden(val) {
      this.clearHiddenTimeout()
      if (val) {
        this.startHiddenTimeout()
      }
      // Reset pagination when switching between visible / hidden order views.
      this.page_offset = 0
      this.selected_order_id = null
    },
    // Clamp page_offset when orders are bumped and the list shrinks below the
    // current offset so the bump bar never targets a non-existent slot.
    displayed_order_ids(new_ids) {
      if (this.page_offset > 0 && this.page_offset >= new_ids.length) {
        this.page_offset = Math.max(0, new_ids.length - 8)
      }
    },
    visible_orders_count(new_val, old_val) {
      if (new_val > old_val) {
        // Check if sound is loaded and unlocked
        if (this.notify_sound) {
           // Reset time to 0. This allows the sound to "restart" 
           // immediately if two orders come in very quickly.
           this.notify_sound.currentTime = 0;
           this.audioUnlocked && this.notify_sound.play().catch(e => {
             console.error("Audio play failed (likely browser interaction policy):", e);
           });
        } else {
          const notify_sound = new Audio(require('@/assets/ding_dong.wav'))
          this.audioUnlocked && notify_sound.play()
        }
        this.unlockAudio();
      }
    }
  },

  methods: {
      ...mapActions({
          load_orders: 'LOAD_ORDERS',
          save_order: 'SAVE_ORDER',
          toggleKdsHold: 'toggleKdsHold',
          syncAllCompleted: 'SYNC_ALL_COMPLETED_TO_LOCAL'
      }),
      unlockAudio() {
        this.audioUnlocked = true
      },

      startHiddenTimeout() {
        const minutes = parseFloat(this.settings.hidden_orders_timeout)
        if (!minutes || minutes <= 0) return
        this.hidden_timeout_id = setTimeout(() => {
          if (this.show_hidden) {
            this.$store.commit('SET_SHOW_HIDDEN', false)
            this.$root.toast.show({ message: 'Hidden orders screen auto-closed due to inactivity', type: 'info' })
          }
        }, minutes * 60 * 1000)
      },

      clearHiddenTimeout() {
        if (this.hidden_timeout_id) {
          clearTimeout(this.hidden_timeout_id)
          this.hidden_timeout_id = null
        }
      },

      resetHiddenTimeout() {
        if (this.show_hidden) {
          this.clearHiddenTimeout()
          this.startHiddenTimeout()
        }
      },

      handleBumpBarInput(e) {
      if (this.settings_dialog) return;

      if (e.ctrlKey || e.altKey || e.metaKey) return;

      const action_name = Object.keys(this.bumpbar_map).find(
        key => this.bumpbar_map[key]?.toLowerCase() === e.key?.toLowerCase()
      );

      if (!action_name) return;

      e.preventDefault();
      this.resetHiddenTimeout();
      
      if (!action_name.startsWith('BUMP_')) {
        this.selected_order_id = null;
      }
      
      this.executeAction(action_name);
    },

    executeAction(action) {
      // Keep the inactivity reset timer alive as long as the user is
      // actively pressing buttons while the view is in a paginated state.
      if (this.page_offset > 0) {
        this.startScrollResetTimer()
      }

      switch (action) {
        case 'BUMP_1': this.processOrderSlot(0); break;
        case 'BUMP_2': this.processOrderSlot(1); break;
        case 'BUMP_3': this.processOrderSlot(2); break;
        case 'BUMP_4': this.processOrderSlot(3); break;
        case 'BUMP_5': this.processOrderSlot(4); break;
        case 'BUMP_6': this.processOrderSlot(5); break;
        case 'BUMP_7': this.processOrderSlot(6); break;
        case 'BUMP_8': this.processOrderSlot(7); break;

        case 'NAV_LEFT': 
          this.scrollPage('left'); 
          break;
        case 'NAV_RIGHT': 
          this.scrollPage('right'); 
          break;
        
        case 'RECALL_LAST':
           if (this.$refs.navbar) this.$refs.navbar.undo();
           break;

        case 'ACTION_REFRESH':
           if (this.$refs.navbar) this.$refs.navbar.reload_orders();
           break;

        case 'SPLIT_ON_OFF':
          this.$root.toast.show({message: "⚠️ No Action", type: 'info'});
          break;
        case 'QUE_SELECT':
          {
            let current_hidden = this.show_hidden;
            this.$store.commit('SET_SHOW_HIDDEN', !current_hidden);
            
            this.$root.toast.show({ 
              message: !current_hidden ? "👁️ Showing Hidden Orders" : "🚫 Hiding Hidden Orders", 
              type: 'info' 
            });
            break;
          }
        case 'QUE_ON_OFF': 
          if (this.settings.enable_multi_kds_setup) {
            this.toggleKdsHold({
              on_success: (status) => {
                const msg = status ? "⛔ KDS PAUSED (Bump Bar)" : "✅ KDS RESUMED (Bump Bar)"
                this.$root.toast.show({ message: msg, type: status ? 'error' : 'success' })
              }
            });
          }
          break;
        case 'SUM_ON_OFF':
          window.location.reload(true); // hard reload
          break;
          //  this.$root.toast.show({message: "⚠️ No Action", type: 'info'});
      }
    },

    processOrderSlot(slot) {
      // Resolve the absolute index by adding the current page offset.
      // page_offset is local data only — Vuex state is never touched here.
      const effective_index = this.page_offset + slot
      const target_orders = this.show_hidden ? this.hidden_orders : this.visible_orders
      const order = target_orders[effective_index]

      if (!order) {
        this.$root.toast.show({ message: `Slot ${slot + 1} is empty`, type: 'info' })
        return
      }

      const new_status = !order.hidden
      const id_to_use = order.offline_pos_name || order._id

      if (this.selected_order_id === id_to_use) {
        this.$store.commit('SET_ORDER_STATUS', { order_id: id_to_use, status: new_status })
        this.$store.dispatch('SYNC_ORDER_STATUS_TO_LOCAL', { order_id: id_to_use })
        this.selected_order_id = null

        this.save_order({
          order_id: id_to_use,
          on_success: () => {
            this.$root.toast.show({ message: `Order #${effective_index + 1} Bumped!`, type: 'success' })
          },
          on_fail: (e, result) => {
            this.$store.commit('SET_ORDER_STATUS', { order_id: id_to_use, status: order.hidden })
            this.$root.toast.show({ message: result, type: 'error' })
          }
        })
      } else {
        this.selected_order_id = id_to_use
        this.scrollToOrder(id_to_use)
      }
    },

    scrollPage(direction) {
      // Jump size comes from settings (default 8 if not configured).
      const jump = Math.max(1, parseInt(this.settings.scroll_jump_size) || 3)
      const total = this.displayed_order_ids.length
      const container = document.querySelector('.kds-screen')

      const amount = Math.max(1, parseInt(this.settings.scroll_amount) || 350)

      if (direction === 'right') {
        // Cap so page_offset never exceeds the last possible slot.
        this.page_offset = Math.min(this.page_offset + jump, Math.max(0, total - 1))
        container && container.scrollBy({ left: amount, behavior: 'smooth' })
      } else {
        this.page_offset = Math.max(0, this.page_offset - jump)
        container && container.scrollBy({ left: -amount, behavior: 'smooth' })
      }

      this.selected_order_id = null

      // Start (or restart) the 10-second inactivity reset timer whenever
      // the view is in a paginated state. Clear it if we returned to page 0.
      if (this.page_offset > 0) {
        this.startScrollResetTimer()
      } else {
        this.clearScrollResetTimer()
      }
    },

    async connectSerial() {
      try {
        const ports = await navigator.serial.getPorts()
        if (!ports.length) {
          this.$root.toast.show({ message: 'No serial port granted. Open Settings → Connect to Bump Bar first.', type: 'warning' })
          return
        }
        this.serialPort = ports[0]
        await this.serialPort.open({ baudRate: this.settings.serial_baud_rate || 9600 })
        this.startSerialReadLoop()
      } catch (e) {
        this.$root.toast.show({ message: `Serial connect failed: ${e.message}`, type: 'error' })
      }
    },

    async startSerialReadLoop() {
      this.serialReading = true
      const decoder = new TextDecoder()
      const reader = this.serialPort.readable.getReader()
      this.serialReader = reader
      try {
        while (this.serialReading) {
          const { value, done } = await reader.read()
          if (done) break
          const text = decoder.decode(value)
          for (const char of text) {
            if (this.settings_dialog) continue
            const action_name = Object.keys(this.bumpbar_map).find(
              key => this.bumpbar_map[key]?.toLowerCase() === char.toLowerCase()
            )
            if (!action_name) continue
            this.resetHiddenTimeout()
            if (!action_name.startsWith('BUMP_')) this.selected_order_id = null
            this.executeAction(action_name)
          }
        }
      } catch (e) {
        if (this.serialReading) {
          this.$root.toast.show({ message: `Serial port disconnected: ${e.message}`, type: 'warning' })
        }
      } finally {
        reader.releaseLock()
      }
    },

    async disconnectSerial() {
      this.serialReading = false
      if (this.serialReader) {
        try { await this.serialReader.cancel() } catch (_) { /* port already closed */ }
        this.serialReader = null
      }
      if (this.serialPort) {
        try { await this.serialPort.close() } catch (_) { /* port already closed */ }
        this.serialPort = null
      }
    },

    // ── Scroll inactivity reset ──────────────────────────────────────────────
    // Starts (or restarts) a 10-second countdown. When it expires the view
    // returns to its initial state: page_offset → 0, container scrolled back
    // to the left edge. Absolute Vuex order data is never mutated.
    startScrollResetTimer() {
      this.clearScrollResetTimer()
      this.scroll_reset_timeout_id = setTimeout(() => {
        this.resetScrollState()
      }, 10000)
    },

    clearScrollResetTimer() {
      if (this.scroll_reset_timeout_id) {
        clearTimeout(this.scroll_reset_timeout_id)
        this.scroll_reset_timeout_id = null
      }
    },

    resetScrollState() {
      this.page_offset = 0
      this.selected_order_id = null
      this.clearScrollResetTimer()
      const container = document.querySelector('.kds-screen')
      if (container) {
        container.scrollTo({ left: 0, behavior: 'smooth' })
      }
    },
    // ────────────────────────────────────────────────────────────────────────

    scrollToOrder(order_id) {
      this.$nextTick(() => {
        const refArr = this.$refs['order_' + order_id]
        if (!refArr || !refArr.length) return
        const el = refArr[0].$el
        const container = document.querySelector('.kds-screen')
        if (!el || !container) return

        const containerRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()

        // Check if the order is not fully visible in the container
        if (elRect.right > containerRect.right || elRect.left < containerRect.left) {
          const scrollLeft = container.scrollLeft + (elRect.left - containerRect.left) - 20
          container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
        }
      })
    }
  },

  mounted () {
    this.load_orders(false)
    const me = this
    setInterval(() => {
      me.load_orders(true)
    }, (this.settings?.heart_beat || 20) * 1000)
    // Re-detect the auth state periodically so a session that expires mid-shift falls back to
    // the API key by itself, instead of leaving the screen silently unauthenticated.
    setInterval(setup_api, 5 * 60 * 1000)
    document.addEventListener('click', this.unlockAudio, { once: true })
    document.addEventListener('keydown', this.unlockAudio, { once: true })
    if (this.settings.bump_bar_mode === 'Web Serial') {
      this.connectSerial()
    } else {
      window.addEventListener('keydown', this.handleBumpBarInput)
    }

    // Sync completed orders to local server every 30 seconds
    this.completion_sync_interval_id = setInterval(() => {
      this.syncAllCompleted()
    }, 30 * 1000)
  },

  beforeDestroy() {
    if (this.settings.bump_bar_mode === 'Web Serial') {
      this.disconnectSerial()
    } else {
      window.removeEventListener('keydown', this.handleBumpBarInput)
    }
    this.clearHiddenTimeout()
    this.clearScrollResetTimer()
    if (this.completion_sync_interval_id) {
      clearInterval(this.completion_sync_interval_id)
      this.completion_sync_interval_id = null
    }
  },
}
</script>

<style>
  .kds-screen {
    height: 100vh;
    overflow-x: auto;
    max-width: unset;
  }
  .dark-mode {
    background-image: linear-gradient(rgb(100, 118, 152), rgb(71, 88, 118), rgb(12, 16, 28));
  }
</style>